import {IObjectData, IRouteDefinition, TDataSourceType} from "../../index";
import {TabManager} from "../tab-list/TabManager";
import {getRawSyncFieldValue} from "../../util/misc";
import {global} from "../../appContext";
import {nsDataSource} from "../../util/nsDataSource";
import {ObjectTypeId} from "../../constants";
import getImportFormatType = nsDataSource.getImportFormatType;

/** Optional PageController hook after object-driven tab show() updates. */
interface ITabAvailabilityHost extends TabManager {
    onTabsAvailabilityChanged?(): void;
}

/**
 * Applies object-detail tab visibility for PageShell object routes
 * (logic formerly in route/details/AbstractObject).
 */
export function applyPageShellObjectData(
    tabManager: TabManager,
    data: [IObjectData],
    shellRouteDef: { manual?: boolean },
): Promise<void> {
    const user = data[0];
    const org = global.login.selectedOrganization();
    if (!org) {
        return Promise.resolve();
    }
    const importformats = org.importformats;
    const importformat = importformats
            .find(o => String(o.id) == getRawSyncFieldValue('AuthDataSource', user.syncfields))
        ?? org.importformat;
    const userDatasource: TDataSourceType = importformat ? getImportFormatType(importformat) : 'hr';
    const userIdentityType = getRawSyncFieldValue('IdentityType', user.syncfields);

    tabManager.getAllTabs().forEach(tab => {
        const tab_identityType = tab.__routeDef.identityType;
        const tab_datasource = tab.__routeDef.datasource;
        let matchesDatasource = true;
        if (tab_datasource && userDatasource) {
            matchesDatasource = tab_datasource.includes(userDatasource);
        }
        let matchesIdentity = true;
        if (tab_identityType && userIdentityType) {
            matchesIdentity = tab_identityType.includes(userIdentityType);
        }
        tab.show(matchesDatasource && matchesIdentity);
    });

    const host = tabManager as ITabAvailabilityHost;
    host.onTabsAvailabilityChanged?.();

    return Promise.resolve();
}

/** Object-detail (read) shells that expose below-content action buttons — not create/edit. */
export function supportsPageShellObjectActions(shellRouteDef: IRouteDefinition): boolean {
    const content = shellRouteDef.content;
    if (content === 'component/object/edit' || content === 'component/object/create') {
        return false;
    }
    const typeId = shellRouteDef.objectTypeId;
    if (typeId === ObjectTypeId.User || typeId === ObjectTypeId.ApiUser) {
        return true;
    }
    if (shellRouteDef.manual && (typeId === ObjectTypeId.Group || typeId === ObjectTypeId.Department)) {
        return true;
    }
    return false;
}

/** Create/edit shells keep count/error badges on tabs only — not mirrored onto the main heading. */
export function suppressesPageShellHeadingBadges(shellRouteDef: IRouteDefinition): boolean {
    switch (shellRouteDef.content) {
        case 'component/object/create':
        case 'component/object/edit':
        case 'component/ruleset/mutable/create':
        case 'component/ruleset/mutable/edit':
        case 'component/customer/general/create':
        case 'component/customer/general/edit':
        case 'component/systemaccess/create':
        case 'component/systemaccess/edit':
        case 'component/export-template/create':
        case 'component/export-template/edit':
            return true;
        default:
            return false;
    }
}
